import { useCallback, useEffect, useRef, useState } from 'react';
import { newIdempotencyKey } from './ui';

// ─────────────────────────────────────────────────────────────────────────────
// Finance-core shared hooks.
// ─────────────────────────────────────────────────────────────────────────────

/** Stable JSON of a body so equal payloads hash equal regardless of key order. */
function stableStringify(value) {
	if (value === null || typeof value !== 'object') return JSON.stringify(value);
	if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
	const keys = Object.keys(value).sort();
	return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(',')}}`;
}

/**
 * Correct Idempotency-Key lifecycle for finance money mutations.
 *
 * The backend stores every final response (2xx AND 4xx) under
 * key + hash(method|path|body). Reusing ONE key for the whole modal session —
 * the pattern the codebase previously documented — breaks two ways:
 *   • a fixed key + an EDITED body ⇒ 409 "Idempotency-Key already used for a
 *     different request" (the user is trapped until they reopen the modal);
 *   • a fixed key + an identical retry after a 4xx ⇒ the stored error is
 *     replayed forever.
 *
 * The fix: derive the key from the body. Identical bodies (a double-click, a
 * network-timeout retry) share a key, so the backend safely de-duplicates;
 * the moment the user changes anything, a fresh key is minted, so an edited
 * resubmit is a brand-new request. `rotate()` forces a fresh key for the next
 * submit — used after a success, or to deliberately retry the same body (e.g.
 * resubmitting with an SoD override reason).
 *
 * Usage:
 *   const { keyFor, rotate } = useIdempotentSubmit();
 *   await create({ path, body, idempotencyKey: keyFor(body) }).unwrap();
 *   rotate(); // on success — next submission is a fresh intent
 */
export function useIdempotentSubmit() {
	const ref = useRef({ hash: null, key: null });

	const keyFor = useCallback((body) => {
		let hash;
		try {
			hash = stableStringify(body);
		} catch (e) {
			hash = null; // un-serializable (e.g. File) → always a fresh key
		}
		if (hash === null || ref.current.hash !== hash || !ref.current.key) {
			ref.current = { hash, key: newIdempotencyKey() };
		}
		return ref.current.key;
	}, []);

	const rotate = useCallback(() => {
		ref.current = { hash: null, key: null };
	}, []);

	return { keyFor, rotate };
}

/**
 * Debounced mirror of a value. Drives search inputs so a list refetches once
 * the user pauses typing instead of firing a request per keystroke.
 */
export function useDebouncedValue(value, delay = 300) {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(t);
	}, [value, delay]);
	return debounced;
}
